export interface OrderStatusInput {
    status: string | null;
    filingType: string | null;
    filingLabel: string | null;
    companyName: string | null;
    serviceYear: string | null;
    /** Set for an early (pre-ordered) annual report: the year, opening date, and processing window. */
    preorder?: {year: string; opensOn: string; window: string} | null;
}

/** Statuses a customer can still cancel from the receipt page. */
export const CANCELABLE_STATUSES = ['pending', 'failed', 'scheduled', 'card_declined'];

export interface OrderStatusText {
    statusMessage: string;
    infoMessage: string;
}

/** Receipt-page wording per order status and filing service (annual report, reinstatement, dissolution). */
export function orderStatusText(receipt: OrderStatusInput): OrderStatusText {
    const company = receipt.companyName?.trim() || 'Your company';
    const annualReport = receipt.filingType === 'annual_report';

    switch (receipt.status) {
        case 'scheduled':
            return {
                statusMessage: `Your ${receipt.preorder?.year ?? receipt.serviceYear} annual report is scheduled.`,
                infoMessage: receipt.preorder
                    ? `Florida opens ${receipt.preorder.year} annual report filing on ${receipt.preorder.opensOn}. We will process your filing between ${receipt.preorder.window}, and your card will be charged at that time. Your card has not been charged and will not be charged before ${receipt.preorder.opensOn}.`
                    : 'Your filing is scheduled. Your card will be charged when it is processed.',
            };
        case 'card_declined':
            return {
                statusMessage: 'Your order is on hold.',
                infoMessage: 'Your bank declined the charge for this filing. Please use the link in the email we sent you to update your card, or contact support@fcfiling.com.',
            };
        case 'pending':
        case 'processing':
        case 'failed':
            return {
                statusMessage: 'Your order is being processed.',
                infoMessage: annualReport
                    ? 'Please allow 24 hours for your filing to be completed. Upon completion, you will receive an email confirmation and copy of your filing.'
                    : 'Most filings post within 2-3 business days. Older reinstatements may require additional state review. Upon completion, you will receive an email confirmation.',
            };
        case 'finalizing':
            return {statusMessage: 'Your order is being finalized.', infoMessage: ''};
        case 'complete':
            return {
                statusMessage: 'Your order is complete.',
                infoMessage: receipt.filingType === 'reinstatement'
                    ? `${company} has been reinstated with the Florida Department of State.`
                    : receipt.filingType === 'dissolution'
                        ? `${company} has been dissolved with the Florida Department of State.`
                        : annualReport
                            ? `Your corporation has been renewed for ${receipt.serviceYear}!`
                            : `Your ${receipt.filingLabel || 'filing'} has been completed.`,
            };
        case 'canceled':
            return {statusMessage: 'Your order has been cancelled.', infoMessage: 'Log-in to your profile below to view your completed filings.'};
        default:
            return {statusMessage: 'Your order is in an unknown status.', infoMessage: ''};
    }
}
